import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiCalendar, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { buildObraTimeline, initialAgendaMonth, monthsWithEvents } from "./portfolio/obraTimeline";
import { buildAgendaEvents, buildMonthRange, monthLabelOf } from "./portfolio/agendaModel";
import { buildIcsCalendar, downloadIcs, icsFileName } from "./portfolio/icsExport";
import { obraHitos } from "./hitosModel";
import "./ObraPlanView.scss";

// Pestañas «Cronograma» y «Agenda» de la ficha: la planificación de ESTA obra.
// Cronograma = mini-Gantt (barra de obra, montaje de mobiliario, hoy y cada fecha con
// la situación de su hito); Agenda = las mismas fechas en un calendario mensual,
// exportables a Outlook.
// Pulsar una fecha lleva a su campo en la ficha para editarla o confirmarla.

// Reparte marcadores en carriles para que dos fechas cercanas no se pisen.
const MIN_GAP_PCT = 6;
function assignLanes(items) {
    const lanes = [];
    return items.map((item) => {
        let lane = lanes.findIndex((lastLeft) => item.left - lastLeft >= MIN_GAP_PCT);
        if (lane === -1) { lane = lanes.length; lanes.push(item.left); } else lanes[lane] = item.left;
        return { ...item, lane };
    });
}

// Situación del hito; «is-alert» solo si cuenta como alerta (no en obras completadas o archivadas).
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
// Scroll infinito de la agenda: al acercarse a un borde se añaden meses de este bloque
// (mismos valores que la Agenda general). Se arranca con margen para poder desplazarse.
const INFINITE_MONTHS_STEP = 6;
const INFINITE_EDGE_PX = 400;
const INITIAL_EXTRA_MONTHS = { before: 3, after: 3 };
const keyOf = ({ year, month }) => `${year}-${month}`;

const statusClass = (hito) => (hito ? ` is-${hito.status}${hito.alert ? " is-alert" : ""}` : "");

// Hitos críticos SIN fecha: no caben en el eje ni en la lista por fechas, pero son los
// que más importan cuando piden datos. Se enseñan aparte y llevan a su campo.
function UndatedHitos({ hitos, onGoToField }) {
    if (!hitos.length) return null;
    return (
        <div className="obras-plan__undated">
            <span>Sin fecha</span>
            {hitos.map((hito) => (
                <button
                    key={hito.field}
                    type="button"
                    className={`obras-hito-chip tone-${hito.tone}`}
                    title={`${hito.label}: ${hito.message || hito.statusLabel}. Clic para ir al campo.`}
                    onClick={() => onGoToField(hito.field)}
                >
                    {hito.label}{hito.alert ? ` · ${hito.statusLabel}` : ""}
                </button>
            ))}
        </div>
    );
}

// `obra` lleva los cambios sin guardar (lo que se ve); `savedObra`, lo guardado, que es
// lo único que se exporta a Outlook: nunca se mandan fechas que aún no están en la BD.
export default function ObraPlanView({ obra, savedObra = obra, mode, today, formatDate, onGoToField }) {
    const timeline = useMemo(() => buildObraTimeline(obra, today), [obra, today]);
    const events = useMemo(() => buildAgendaEvents([obra], today), [obra, today]);
    const savedEvents = useMemo(() => buildAgendaEvents([savedObra], today), [savedObra, today]);
    // Una obra sin guardar tiene un identificador provisional: sus eventos cambiarían de
    // UID al guardarla y Outlook los duplicaría.
    const canExport = !savedObra._isNew && savedEvents.length > 0;
    const hasUnsaved = obra !== savedObra && events.some((event) => savedEvents.every((saved) => saved.type.id !== event.type.id || saved.date !== event.date));
    const obraName = [obra.codigoTienda, obra.tienda].filter(Boolean).join(" · ");
    const undated = useMemo(() => obraHitos(obra, today).filter((hito) => !hito.date), [obra, today]);
    // Agenda en calendario con scroll infinito: meses seguidos desde el primer evento (o
    // hoy) hasta el último, ampliables por arriba y por abajo al desplazarse. Se abre en
    // el mes del próximo evento y la cabecera enseña el mes que se está viendo.
    const eventMonths = useMemo(() => monthsWithEvents(events), [events]);
    const [extraMonths, setExtraMonths] = useState(INITIAL_EXTRA_MONTHS);
    const months = useMemo(() => buildMonthRange(events.map((event) => event.date), today, extraMonths), [events, today, extraMonths]);
    const [visibleMonth, setVisibleMonthState] = useState(() => initialAgendaMonth(events, today));
    // Copia síncrona del mes visible: ‹ › encadenados leen el último, no el del render anterior.
    const visibleRef = useRef(visibleMonth);
    const setVisibleMonth = (value) => { visibleRef.current = value; setVisibleMonthState(value); };
    const scrollRef = useRef(null);
    const monthRefs = useRef(new Map());
    const prependAnchorRef = useRef(null);
    // Mes al que hay que desplazarse en cuanto esté pintado: { key, behavior }.
    const pendingScrollRef = useRef({ key: keyOf(initialAgendaMonth(events, today)), behavior: "auto" });

    // Saltos instantáneos: un desplazamiento suave de muchos meses es lento y, mientras
    // dura, el scroll infinito podía añadir meses y mover el destino.
    // La fila de días de la semana está pegada arriba y tapa ese alto del contenedor: un
    // mes «empieza» a la vista cuando su parte superior queda justo debajo de ella.
    const weekheadHeight = () => scrollRef.current?.querySelector(".obras-plan__weekhead")?.offsetHeight || 0;
    const scrollToMonthKey = (key, behavior = "auto") => {
        const scroller = scrollRef.current;
        const block = monthRefs.current.get(key);
        if (!scroller || !block) return false;
        scroller.scrollTo({ top: Math.max(0, block.offsetTop - weekheadHeight()), behavior });
        return true;
    };
    // Ir a un mes: si ya está pintado se desplaza; si no, se amplía el rango hacia su lado
    // y se desplaza al pintarlo.
    const goToMonth = (year, month, behavior = "auto") => {
        const key = keyOf({ year, month });
        setVisibleMonth({ year, month });
        if (scrollToMonthKey(key, behavior)) return;
        pendingScrollRef.current = { key, behavior };
        const first = months[0];
        const target = year * 12 + month;
        if (first && target < first.year * 12 + first.month) {
            setExtraMonths((current) => ({ ...current, before: current.before + (first.year * 12 + first.month - target) + INFINITE_MONTHS_STEP }));
        } else {
            const last = months[months.length - 1];
            setExtraMonths((current) => ({ ...current, after: current.after + (target - (last.year * 12 + last.month)) + INFINITE_MONTHS_STEP }));
        }
    };

    // Tras añadir meses por arriba se mantiene a la vista el mismo contenido; y si hay un
    // mes pendiente (al abrir la pestaña o al saltar fuera del rango) se va a él.
    useLayoutEffect(() => {
        if (mode !== "agenda") return;
        const scroller = scrollRef.current;
        if (!scroller) return;
        const previous = prependAnchorRef.current;
        if (previous) {
            scroller.scrollTop = previous.scrollTop + (scroller.scrollHeight - previous.scrollHeight);
            prependAnchorRef.current = null;
        }
        const pending = pendingScrollRef.current;
        if (pending && scrollToMonthKey(pending.key, pending.behavior)) pendingScrollRef.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [months, mode]);

    const handleAgendaScroll = (event) => {
        const scroller = event.currentTarget;
        // Mes visible: el último cuyo inicio ya ha llegado a la fila de días de la semana.
        const top = scroller.scrollTop + weekheadHeight() + 8;
        let current = months[0];
        months.forEach((monthData) => {
            const block = monthRefs.current.get(monthData.key);
            if (block && block.offsetTop <= top) current = monthData;
        });
        if (current && (current.year !== visibleRef.current.year || current.month !== visibleRef.current.month)) {
            setVisibleMonth({ year: current.year, month: current.month });
        }
        if (prependAnchorRef.current || pendingScrollRef.current) return;
        if (scroller.scrollTop < INFINITE_EDGE_PX) {
            prependAnchorRef.current = { scrollTop: scroller.scrollTop, scrollHeight: scroller.scrollHeight };
            setExtraMonths((value) => ({ ...value, before: value.before + INFINITE_MONTHS_STEP }));
        } else if (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < INFINITE_EDGE_PX) {
            setExtraMonths((value) => ({ ...value, after: value.after + INFINITE_MONTHS_STEP }));
        }
    };

    if (timeline.empty) {
        return (
            <section className="obras-plan">
                <p className="obras-plan__empty">Esta obra todavía no tiene fechas. Añade el inicio, la apertura o algún hito para verla aquí.</p>
                <UndatedHitos hitos={undated} onGoToField={onGoToField} />
            </section>
        );
    }

    if (mode === "agenda") {
        const eventsByDate = events.reduce((acc, event) => {
            (acc[event.date] ||= []).push(event);
            return acc;
        }, {});
        const shiftMonth = (delta) => {
            const next = new Date(visibleRef.current.year, visibleRef.current.month + delta, 1, 12);
            goToMonth(next.getFullYear(), next.getMonth());
        };
        const isCurrent = (target) => target.year === visibleMonth.year && target.month === visibleMonth.month;
        return (
            <section className="obras-plan obras-plan--agenda" aria-label={`Agenda de ${obraName}`}>
                <header className="obras-plan__head">
                    <div className="obras-plan__monthnav">
                        <button type="button" aria-label="Mes anterior" onClick={() => shiftMonth(-1)}><FiChevronLeft /></button>
                        <strong aria-live="polite">{monthLabelOf(visibleMonth.year, visibleMonth.month)}</strong>
                        <button type="button" aria-label="Mes siguiente" onClick={() => shiftMonth(1)}><FiChevronRight /></button>
                        <button type="button" className="obras-plan__today-btn" onClick={() => goToMonth(today.getFullYear(), today.getMonth())}>Hoy</button>
                    </div>
                    <button
                        type="button"
                        className="obras-plan__ics"
                        disabled={!canExport}
                        onClick={() => downloadIcs(icsFileName(`Obra_${savedObra.codigoTienda || ""}_${savedObra.tienda || ""}`), buildIcsCalendar(savedEvents, { name: `Obra ${obraName}` }))}
                        title={savedObra._isNew
                            ? "Guarda la obra antes de exportarla a Outlook"
                            : hasUnsaved
                                ? "Exporta las fechas GUARDADAS; los cambios sin guardar no se incluyen"
                                : "Descarga las fechas de esta obra como calendario .ics para importarlo en Outlook"}
                    >
                        <FiCalendar aria-hidden="true" /> Exportar a Outlook (.ics)
                    </button>
                </header>
                {eventMonths.length ? (
                    <nav className="obras-plan__months" aria-label="Meses con fechas de la obra">
                        {eventMonths.map((target) => (
                            <button
                                key={keyOf(target)}
                                type="button"
                                className={isCurrent(target) ? "is-active" : ""}
                                aria-pressed={isCurrent(target)}
                                onClick={() => goToMonth(target.year, target.month)}
                            >
                                {monthLabelOf(target.year, target.month)} <b>{target.count}</b>
                            </button>
                        ))}
                    </nav>
                ) : null}
                {hasUnsaved ? <p className="obras-plan__note">Hay fechas sin guardar: se ven aquí, pero Outlook recibe las guardadas.</p> : null}
                <div className="obras-plan__scroller" ref={scrollRef} onScroll={handleAgendaScroll}>
                    <div className="obras-plan__weekhead" aria-hidden="true">
                        {WEEKDAYS.map((day) => <span key={day} className="obras-plan__weekday">{day}</span>)}
                    </div>
                    {months.map((monthData) => (
                        <section
                            key={monthData.key}
                            className="obras-plan__monthblock"
                            ref={(node) => { if (node) monthRefs.current.set(monthData.key, node); else monthRefs.current.delete(monthData.key); }}
                            aria-label={monthData.label}
                        >
                            <h4 className="obras-plan__monthsep">{monthData.label}</h4>
                            <div className="obras-plan__calendar" role="grid" aria-label={monthData.label}>
                                {Array.from({ length: monthData.offset }, (_, index) => <div key={`lead-${index}`} className="obras-plan__day is-empty" aria-hidden="true" />)}
                                {monthData.days.map(({ iso, date }) => {
                                    const dayEvents = eventsByDate[iso] || [];
                                    const isToday = iso === timeline.todayIso;
                                    return (
                                        <div key={iso} className={`obras-plan__day${isToday ? " is-today" : ""}${dayEvents.length ? " has-events" : ""}`} role="gridcell" aria-current={isToday ? "date" : undefined}>
                                            <span className="obras-plan__daynum">{date.getDate()}{isToday ? <em>Hoy</em> : null}</span>
                                            {dayEvents.map((event) => (
                                                <button
                                                    key={event.type.id}
                                                    type="button"
                                                    className={`obras-plan__event${statusClass(event.hito)}`}
                                                    title={`${event.type.label}: ${formatDate(event.date)}${event.hito ? ` · ${event.hito.message || event.hito.statusLabel}` : ""}. Clic para ir al campo.`}
                                                    onClick={() => onGoToField(event.type.id)}
                                                >
                                                    <i>{event.type.prefix}</i>
                                                    <span>{event.type.label}</span>
                                                    {event.hito ? <em className={`obras-hito-chip tone-${event.hito.tone}`}>{event.hito.statusLabel}</em> : null}
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })}
                                {Array.from({ length: (7 - ((monthData.offset + monthData.days.length) % 7)) % 7 }, (_, index) => <div key={`trail-${index}`} className="obras-plan__day is-empty" aria-hidden="true" />)}
                            </div>
                        </section>
                    ))}
                </div>
                <UndatedHitos hitos={undated} onGoToField={onGoToField} />
            </section>
        );
    }

    const markers = assignLanes(timeline.items);
    const laneCount = Math.max(1, ...markers.map((marker) => marker.lane + 1));
    return (
        <section className="obras-plan obras-plan--gantt" aria-label={`Cronograma de ${obraName}`}>
            <ul className="obras-plan__facts">
                {timeline.obraBar ? <li><span>Obra</span><strong>{formatDate(obra.inicioObra)} → {formatDate(obra.apertura)} · {timeline.obraBar.days} días</strong></li> : null}
                {timeline.montajeBar ? <li><span>Montaje mobiliario</span><strong>{formatDate(obra.inicioMontajeMob)} → {formatDate(obra.finMontajeMob)} · {timeline.montajeBar.days} días</strong></li> : null}
                <li><span>Periodo mostrado</span><strong>{formatDate(timeline.start)} – {formatDate(timeline.end)}</strong></li>
            </ul>
            <div className="obras-plan__chart" style={{ "--plan-lanes": laneCount }}>
                <div className="obras-plan__ticks" aria-hidden="true">
                    {timeline.ticks.map((tick) => <span key={tick.key} style={{ left: `${tick.left}%` }}>{tick.label}</span>)}
                </div>
                <div className="obras-plan__track">
                    {timeline.ticks.map((tick) => <b key={tick.key} className="obras-plan__gridline" style={{ left: `${tick.left}%` }} aria-hidden="true" />)}
                    {timeline.obraBar ? <span className="obras-plan__bar" style={{ left: `${timeline.obraBar.left}%`, width: `${timeline.obraBar.width}%` }} title={`Obra: ${timeline.obraBar.days} días`} /> : null}
                    {timeline.montajeBar ? <span className="obras-plan__bar obras-plan__bar--montaje" style={{ left: `${timeline.montajeBar.left}%`, width: `${timeline.montajeBar.width}%` }} title={`Montaje de mobiliario: ${timeline.montajeBar.days} días`} /> : null}
                    {timeline.today != null ? <span className="obras-plan__today" style={{ left: `${timeline.today}%` }}><em>Hoy</em></span> : null}
                    {markers.map((marker) => (
                        <button
                            key={marker.id}
                            type="button"
                            className={`obras-plan__marker${statusClass(marker.hito)}`}
                            style={{ left: `${marker.left}%`, "--lane": marker.lane }}
                            title={`${marker.label}: ${formatDate(marker.date)}${marker.hito ? ` · ${marker.hito.statusLabel}` : ""}`}
                            aria-label={`${marker.label}: ${formatDate(marker.date)}${marker.hito ? `, ${marker.hito.statusLabel}` : ""}`}
                            onClick={() => onGoToField(marker.id)}
                        >
                            {marker.prefix}
                        </button>
                    ))}
                </div>
            </div>
            <ul className="obras-plan__legend">
                {markers.map((marker) => (
                    <li key={marker.id}>
                        <button
                            type="button"
                            className={`obras-plan__legend-item${statusClass(marker.hito)}`}
                            title={`${marker.label}: ${formatDate(marker.date)}${marker.hito ? ` · ${marker.hito.message || marker.hito.statusLabel}` : ""}. Clic para ir al campo.`}
                            onClick={() => onGoToField(marker.id)}
                        >
                            <i>{marker.prefix}</i>
                            <span>{marker.label}</span>
                            <time dateTime={marker.date}>{formatDate(marker.date)}</time>
                            {marker.hito ? <em className={`obras-hito-chip tone-${marker.hito.tone}`}>{marker.hito.statusLabel}</em> : null}
                        </button>
                    </li>
                ))}
            </ul>
            <UndatedHitos hitos={undated} onGoToField={onGoToField} />
        </section>
    );
}
